/**
 * The Configuration sections and their addresses — the routing half of SettingsPage, split out so
 * it can be imported without it.
 *
 * SettingsPage itself imports all seven panes, so anything importing a path helper from there
 * pulls the entire configuration screen in with it. That matters for the header, which is rendered
 * on every page and needs only one constant: without this split, visiting the dashboard or the
 * matching page loads the whole of Configuration too.
 */
export type PaneName = 'sources' | 'add' | 'ingest' | 'buy' | 'keys' | 'people' | 'account';

export const PANES: { name: PaneName; label: string }[] = [
  { name: 'sources', label: 'Providers' },
  { name: 'add', label: 'Add stats' },
  { name: 'ingest', label: 'Email & Sheets' },
  { name: 'buy', label: 'Buy side' },
  { name: 'keys', label: 'APIs' },
  { name: 'people', label: 'People & access' },
  { name: 'account', label: 'My account' },
];

const isPane = (v: string): v is PaneName => PANES.some((p) => p.name === v);

export const CONFIG_ROOT = '/configuration';

/** '/configuration' → the first section; '/configuration/<name>' → that one. */
export function paneFromPath(path: string): PaneName | null {
  if (path === CONFIG_ROOT || path === `${CONFIG_ROOT}/`) return 'sources';
  const m = new RegExp(`^${CONFIG_ROOT}/([a-z]+)/?$`).exec(path);
  return m && isPane(m[1]!) ? m[1] : null;
}

export function pathForPane(pane: PaneName): string {
  return `${CONFIG_ROOT}/${pane}`;
}

/**
 * Addresses this screen used to have: /settings/<pane> (the page before it was
 * renamed) and the modal-era hashes #settings/<pane> and #add. Returns the
 * section they meant, so App can rewrite them to the current path.
 */
export function legacyPane(path: string, hash: string): PaneName | null {
  if (hash === '#add') return 'add';
  if (hash === '#settings' || hash === '#settings/') return 'sources';
  const h = /^#settings\/([a-z]+)$/.exec(hash);
  if (h && isPane(h[1]!)) return h[1];
  if (path === '/settings' || path === '/settings/') return 'sources';
  const m = /^\/settings\/([a-z]+)\/?$/.exec(path);
  return m && isPane(m[1]!) ? m[1] : null;
}
